import { Close, CreditCard, Keyboard, Money } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { payOrder } from '../../../api/order/payOrderNew';
import { Order, PromotionApplied } from '../../../apiTypes';
import KeyBoard from '../../../components/KeyBoard';
import PrimaryButton from '../../../components/PrimaryButton';
import StyledTextField from '../../../components/styled/StyledTextField';
import { t } from '../../../constants/translations';
import { useAdmin } from '../../../contexts/admin.context';
import { useCafe } from '../../../contexts/cafe.context';
import { useLocale } from '../../../contexts/locale.context';
import { useSnackbar } from '../../../contexts/snackbar.context';
import currency from '../../../utils/currency';
import getBillCalc from '../../../utils/getBillCalc';
import { useLocalStorage } from '../../../utils/useLocalStorage';
import useMedia from '../../../utils/useMedia';
import BillPrintModal from '../BillPrintModal';

type Props = {
  order: DbDocument<Order>;
  promotionsApplied: PromotionApplied[];
  onClose: () => void;
};

type Method = 'cash' | 'card';

type Focesed = 'service' | 'discount' | 'cash' | null;

const PayModal = ({ order, onClose, promotionsApplied }: Props) => {
  const { lang } = useLocale();
  const { cafe, cafeSlug, getCurrency } = useAdmin();
  const { addSnackbar } = useSnackbar();
  const { isMobile } = useMedia();
  const { dishesMap, wallets, worker, currentPosShift, legalEntities, legalEntitiesMap } = useCafe();

  const [focused, setFocused] = useState<Focesed>('cash');
  const [keyboard, setKeyboard] = useLocalStorage('keyboard');
  const [isLoading, setIsLoading] = useState(false);
  const [isPrinOpen, setIsPrintOpen] = useState(false);
  const [method, setMethod] = useState<Method>('cash');
  const [cashString, setCashString] = useState<string>('');
  const [service, setService] = useState(cafe?.bill?.defaultService || 0);
  const [discount, setDiscount] = useState(order.discount);
  const cash = Number(cashString) * 100;

  const orderItems = useMemo(() => Object.values(order.items), [order.items]);

  const billCalc = useMemo(
    () =>
      getBillCalc({
        orderItems,
        discount,
        service,
        promotionsApplied,
        legalEntities,
      }),
    [discount, legalEntities, orderItems, promotionsApplied, service]
  );

  const handlePay = useCallback(async () => {
    if (!worker) {
      return;
    }

    setIsLoading(true);

    const { errors } = await payOrder({
      order,
      billCalc,
      worker,
      cafeSlug,
      method,
      wallets,
      discount,
      service,
      currentPosShift,
      dishesMap,
      promotionsApplied,
      legalEntitiesMap,
      defaultLegalEntityId: legalEntities[0]?.id,
    });

    if (errors.length > 0) {
      addSnackbar(typeof errors[0] === 'string' ? errors[0] : t.something_went_wrong[lang], 'error');
      setIsLoading(false);
      return;
    }
    onClose();
  }, [
    addSnackbar,
    billCalc,
    cafeSlug,
    currentPosShift,
    discount,
    dishesMap,
    lang,
    legalEntities,
    legalEntitiesMap,
    method,
    onClose,
    order,
    promotionsApplied,
    service,
    wallets,
    worker,
  ]);

  const handleChangeService = useCallback((value: string) => {
    setService(Number(value) < 100 ? Number(value) : 100);
  }, []);

  const handleChangeDiscount = useCallback((value: string) => {
    setDiscount(Number(value) <= 100 ? Number(value) : 100 || 0);
  }, []);

  const handleChangeCash = useCallback((value: string) => {
    const patern = /^\d+(\.\d{0,2})?$/;
    if (value !== '0' && (value === '' || patern.test(value))) {
      setCashString(value);
    }
  }, []);

  const toggleKeyboard = useCallback(() => {
    setKeyboard(keyboard ? undefined : 'true');
  }, [keyboard, setKeyboard]);

  const keyBoardValue = useMemo(() => {
    if (focused === 'service') {
      return String(service);
    }
    if (focused === 'discount') {
      return String(discount);
    }
    if (focused === 'cash') {
      return cashString;
    }
    return '';
  }, [cashString, discount, focused, service]);

  const handleChangeKeyboard = useCallback(
    (v: string) => {
      if (isLoading) {
        return null;
      }
      if (focused === 'service') {
        return handleChangeService(v);
      }
      if (focused === 'discount') {
        return handleChangeDiscount(v);
      }
      if (focused === 'cash') {
        return handleChangeCash(v);
      }
      return null;
    },
    [focused, handleChangeCash, handleChangeDiscount, handleChangeService, isLoading]
  );

  return (
    <Dialog onClose={onClose} open fullWidth fullScreen={isMobile} maxWidth={keyboard ? 'sm' : 'xs'}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography component="span" variant="h6">{`${t.order[lang]} №${order.number}`}</Typography>

        <div>
          <IconButton onClick={toggleKeyboard} sx={{ mr: 1 }}>
            <Keyboard />
          </IconButton>

          <IconButton aria-label="close" onClick={onClose}>
            <Close />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 5,
          alignItems: isMobile ? 'center' : 'flex-start',
        }}
        data-testid="pay-form"
      >
        <Box width="100%">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{`${t.total[lang]}:`}</Typography>
            <Typography variant="h6" data-testid="total-sum">
              {currency(billCalc.total.itemsTotalSum, cafe?.currency)}
            </Typography>
          </Box>

          {!!billCalc.total.promotionsSum && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 2, ml: 2 }}>
              <Typography>{t.promotions[lang]}:</Typography>
              <Typography data-testid="promotion-sum">
                -{currency(billCalc.total.promotionsSum, cafe?.currency)}
              </Typography>
            </Box>
          )}

          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <TextField
                label={t.discount[lang]}
                value={discount}
                disabled={!worker?.credentials.change_service_discount_percentage || isLoading}
                size="small"
                onFocus={() => setFocused('discount')}
                autoComplete="off"
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                onChange={(e) => handleChangeDiscount(e.target.value)}
                sx={{ width: 140, mt: 1, div: { backgroundColor: (t) => t.palette.background.paper } }}
                data-testid="discount-field"
              />
              <Typography data-testid="discount-sum">
                -{currency(billCalc.total.discountSum, cafe?.currency)}
              </Typography>
            </Box>

            {!!cafe?.bill?.defaultService && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <TextField
                  label={t.service_charge[lang]}
                  value={service}
                  disabled={!worker?.credentials.change_service_discount_percentage || isLoading}
                  size="small"
                  onFocus={() => setFocused('service')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  autoComplete="off"
                  onChange={(e) => handleChangeService(e.target.value)}
                  sx={{ width: 140, mt: 1, div: { backgroundColor: (t) => t.palette.background.paper } }}
                  data-testid="service-charge-field"
                />
                <Typography data-testid="service-charge-sum">
                  {currency(billCalc.total.serviceSum, cafe.currency)}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Typography variant="h5">{`${t.to_pay[lang]}:`}</Typography>
              <Typography variant="h5" data-testid="to-pay-sum">
                {getCurrency(billCalc.total.toPay)}
              </Typography>
            </Box>

            {legalEntities.length > 1 &&
              Object.entries(billCalc.byLegalEntity).map(([key, bill]) => (
                <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color={(t) => t.palette.text.secondary}>
                    {legalEntitiesMap[key]?.name || key}:
                  </Typography>
                  <Typography
                    data-testid="legal-entities-to-pay-sum"
                    variant="body2"
                    color={(t) => t.palette.text.secondary}
                  >
                    {getCurrency(bill.toPay)}
                  </Typography>
                </Box>
              ))}
          </>

          <ToggleButtonGroup
            value={method}
            exclusive
            onChange={(e, v) => {
              if (v === 'cash' || v === 'card') {
                setMethod(v);
              }
            }}
            fullWidth
            sx={{ mt: 3, mb: 3 }}
            disabled={isLoading}
          >
            <ToggleButton value="cash">{t.in_cash[lang]}</ToggleButton>
            <ToggleButton value="card">{t.by_card[lang]}</ToggleButton>
          </ToggleButtonGroup>

          {method === 'cash' && (
            <Box sx={{ mb: 2 }}>
              <StyledTextField
                label={t.cash[lang]}
                fullWidth
                value={cashString}
                onChange={(e) => handleChangeCash(e.target.value)}
                color="secondary"
                disabled={isLoading}
                onFocus={() => setFocused('cash')}
                autoComplete="off"
                data-testid="cash-field"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Money />
                    </InputAdornment>
                  ),
                  endAdornment: <InputAdornment position="end">{cafe?.currency}</InputAdornment>,
                }}
              />
              {cash > billCalc.total.toPay && (
                <Box sx={{ height: 20, mt: 1, ml: 1 }}>
                  <Typography>{`${t.rest[lang]}: ${getCurrency(cash - billCalc.total.toPay)}`}</Typography>
                </Box>
              )}
            </Box>
          )}

          {method === 'card' &&
            legalEntities.length > 1 &&
            Object.entries(billCalc.byLegalEntity).map(([key, bill]) => (
              <StyledTextField
                key={key}
                sx={{ mb: 2 }}
                label={legalEntitiesMap[key]?.name || key}
                fullWidth
                value={(bill.toPay / 100).toFixed(2)}
                disabled
                color="secondary"
                autoComplete="off"
                data-testid="card-field"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CreditCard />
                    </InputAdornment>
                  ),
                  endAdornment: <InputAdornment position="end">{cafe?.currency}</InputAdornment>,
                }}
              />
            ))}

          {method === 'card' && legalEntities.length < 2 && (
            <StyledTextField
              sx={{ mb: 2 }}
              label={t.by_card[lang]}
              fullWidth
              value={(billCalc.total.toPay / 100).toFixed(2)}
              disabled
              color="secondary"
              data-testid="card-field"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CreditCard />
                  </InputAdornment>
                ),
                endAdornment: <InputAdornment position="end">{cafe?.currency}</InputAdornment>,
              }}
            />
          )}
        </Box>
        {!!keyboard && (
          <Box sx={{ bgcolor: (t) => t.palette.background.default, borderRadius: 1, px: 1 }}>
            <KeyBoard value={keyBoardValue} onChange={handleChangeKeyboard} size="small" hasDot />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', p: 3, pt: 0 }}>
        <PrimaryButton onClick={() => setIsPrintOpen(true)} color="secondary">
          {t.print_bill[lang]}
        </PrimaryButton>

        <PrimaryButton
          variant="contained"
          onClick={handlePay}
          loading={isLoading}
          disabled={isLoading || (method === 'cash' && cash < billCalc.total.toPay)}
        >
          {t.pay[lang]}
        </PrimaryButton>
      </DialogActions>

      {isPrinOpen && (
        <BillPrintModal
          service={service}
          discount={discount}
          onClose={() => setIsPrintOpen(false)}
          order={order}
          promotionsApplied={promotionsApplied}
        />
      )}
    </Dialog>
  );
};

export default PayModal;
